import React, { useEffect, useState } from "react";
import {
  Grid,
  Container,
  CircularProgress,
  Alert,
  Box,
  Fab,
} from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import BlogCard from "./BlogCard";
import { fetchBlogs } from "../Api/blogFetch";
import NavBar from "./NavBar";
const BlogList = () => {
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [displayedBlogs, setDisplayedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const ITEMS_PER_PAGE = 6;

  useEffect(() => {
    const getBlogs = async () => {
      try {
        const response = await fetchBlogs();
        setBlogs(response.data);
        setFilteredBlogs(response.data);
        setDisplayedBlogs(response.data.slice(0, ITEMS_PER_PAGE));
      } catch (err) {
        setError(err.message || "Something went wrong while fetching blogs");
      } finally {
        setLoading(false);
      }
    };

    getBlogs();
  }, []);

  useEffect(() => {
    // Filter blogs based on both category and search term
    let filtered = [...blogs];

    if (selectedCategory) {
      filtered = filtered.filter((blog) => blog.category === selectedCategory);
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (blog) =>
          blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          blog.content.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredBlogs(filtered);
    setDisplayedBlogs(filtered.slice(0, ITEMS_PER_PAGE));
    setHasMore(filtered.length > ITEMS_PER_PAGE);
  }, [selectedCategory, blogs, searchTerm]);

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
  };

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
  };

  const fetchMoreData = () => {
    const currentLength = displayedBlogs.length;
    const nextBatch = filteredBlogs.slice(
      currentLength,
      currentLength + ITEMS_PER_PAGE
    );

    // Simulate a delay for the loader
    setTimeout(() => {
      setDisplayedBlogs([...displayedBlogs, ...nextBatch]);
      setHasMore(currentLength + ITEMS_PER_PAGE < filteredBlogs.length);
    }, 1000); // Simulates loading time
  };

  // Scroll to top logic
  const handleScroll = () => {
    if (window.scrollY > 200) {
      setShowScrollTop(true);
    } else {
      setShowScrollTop(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ marginTop: "400px", textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ marginTop: "40px" }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <>
      <NavBar
        onCategorySelect={handleCategorySelect}
        onSearch={handleSearch}
        blogs={blogs}
      />
      <Container
        maxWidth="lg"
        sx={{
          marginTop: "100px",
          overflow: "hidden", // Prevents any content overflow
          padding: { xs: "0 8px", sm: "0 16px" }, // Adjust padding for responsiveness
        }}
      >
        <InfiniteScroll
          dataLength={displayedBlogs.length}
          next={fetchMoreData}
          hasMore={hasMore}
          loader={
            <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
              <CircularProgress />
            </Box>
          }
        >
          <Grid
  container
  spacing={{ xs: 2, sm: 4 }} // Adjust spacing dynamically for mobile & desktop
  sx={{
    width: '100%',
    margin: 0,
    padding: { xs: '16px 8px', sm: '32px 16px' }, // Adjust top & left padding for mobile
  }}
>
  {displayedBlogs.map((blog) => (
    <Grid 
      item 
      key={blog._id} 
      xs={12} 
      sm={6} 
      md={4}
      sx={{
        display: 'flex',
        justifyContent: 'center', // Center content properly inside each grid item
        padding: { xs: '8px', sm: '16px' }, // Add padding to each card
      }}
    >
      <BlogCard
        title={blog.title}
        description={blog.content}
        image={blog.blog_img}
        link={`/blog/${blog._id}`}
        sx={{ width: '100%', padding: { xs: '8px', sm: '16px' } }} // Extra padding on cards
      />
    </Grid>
  ))}
</Grid>

        </InfiniteScroll>
      </Container>

      {showScrollTop && (
        <Fab
          color="primary"
          onClick={scrollToTop}
          sx={{
            position: "fixed",
            bottom: { xs: 16, md: 32 }, // Adjust for mobile (xs) and desktop (md)
            right: { xs: 16, md: 32 }, // Adjust for mobile (xs) and desktop (md)
            zIndex: 1000, // Ensure it appears above other content
          }}
        >
          <ArrowUpwardIcon />
        </Fab>
      )}
    </>
  );
};

export default BlogList;
